'use client'

import { useLingui } from '@lingui/react/macro'
import { animate, AnimationPlaybackControls, useMotionValue } from 'framer-motion'
import { CSSProperties, useEffect, useId, useRef } from 'react'

// Type definitions
interface ResponsiveImage {
  src: string
  alt?: string
  srcSet?: string
}

interface AnimationConfig {
  preview?: boolean
  scale: number
  speed: number
}

interface NoiseConfig {
  opacity: number
  scale: number
}

interface ShadowOverlayProps {
  type?: 'preset' | 'custom'
  presetIndex?: number
  customImage?: ResponsiveImage
  sizing?: 'fill' | 'stretch'
  color?: string
  animation?: AnimationConfig
  noise?: NoiseConfig
  style?: CSSProperties
  className?: string
}

function mapRange(
  value: number,
  fromLow: number,
  fromHigh: number,
  toLow: number,
  toHigh: number
): number {
  if (fromLow === fromHigh) {
    return toLow
  }
  const percentage = (value - fromLow) / (fromHigh - fromLow)
  return toLow + percentage * (toHigh - toLow)
}

const useInstanceId = (): string => {
  const id = useId()
  const cleanId = id.replace(/:/g, '')
  const instanceId = `shadowoverlay-${cleanId}`
  return instanceId
}

export function Component({
  sizing = 'fill',
  color = 'rgba(128, 128, 128, 1)',
  animation,
  noise,
  style,
  className
}: ShadowOverlayProps) {
  const id = useInstanceId()
  const animationEnabled = animation && animation.scale > 0
  const feColorMatrixRef = useRef<SVGFEColorMatrixElement>(null)
  const hueRotateMotionValue = useMotionValue(180)
  const hueRotateAnimation = useRef<AnimationPlaybackControls | null>(null)

  const displacementScale = animation ? mapRange(animation.scale, 1, 100, 20, 100) : 0
  const animationDuration = animation ? mapRange(animation.speed, 1, 100, 1000, 50) : 1
  const { i18n } = useLingui()

  useEffect(() => {
    if (feColorMatrixRef.current && animationEnabled) {
      if (hueRotateAnimation.current) {
        hueRotateAnimation.current.stop()
      }
      hueRotateMotionValue.set(0)
      hueRotateAnimation.current = animate(hueRotateMotionValue, 360, {
        duration: animationDuration / 25,
        repeat: Infinity,
        repeatType: 'loop',
        repeatDelay: 0,
        ease: 'linear',
        delay: 0,
        onUpdate: (value: number) => {
          if (feColorMatrixRef.current) {
            feColorMatrixRef.current.setAttribute('values', String(value))
          }
        }
      })

      return () => {
        if (hueRotateAnimation.current) {
          hueRotateAnimation.current.stop()
        }
      }
    }
  }, [animationEnabled, animationDuration, hueRotateMotionValue])

  return (
    <div
      className={className}
      style={{
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
        height: '100%',
        borderRadius: '20px',
        ...style
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: -displacementScale,
          filter: animationEnabled ? `url(#${id}) blur(4px)` : 'none'
        }}
      >
        {animationEnabled && (
          <svg style={{ position: 'absolute' }}>
            <defs>
              <filter id={id}>
                <feTurbulence
                  result='undulation'
                  numOctaves='2'
                  baseFrequency={`${mapRange(animation.scale, 0, 100, 0.001, 0.0005)},${mapRange(animation.scale, 0, 100, 0.004, 0.002)}`}
                  seed='0'
                  type='turbulence'
                />
                <feColorMatrix
                  ref={feColorMatrixRef}
                  in='undulation'
                  type='hueRotate'
                  values='180'
                />
                <feColorMatrix
                  in='dist'
                  result='circulation'
                  type='matrix'
                  values='4 0 0 0 1  4 0 0 0 1  4 0 0 0 1  1 0 0 0 0'
                />
                <feDisplacementMap
                  in='SourceGraphic'
                  in2='circulation'
                  scale={displacementScale}
                  result='dist'
                />
                <feDisplacementMap
                  in='dist'
                  in2='undulation'
                  scale={displacementScale}
                  result='output'
                />
              </filter>
            </defs>
          </svg>
        )}
        <div
          style={{
            backgroundColor: color,
            maskImage: `url('https://framerusercontent.com/images/ceBGguIpUU8luwByxuQz79t7To.png')`,
            maskSize: sizing === 'stretch' ? '100% 100%' : 'cover',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
            width: '100%',
            height: '100%'
          }}
        />
      </div>

      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          zIndex: 10
        }}
      >
        <h1 className='md:text-6xl text-5xl lg:text-7xl font-bold text-center text-foreground relative z-20'>
          {i18n._('Frequently Asked Questions')}
        </h1>
      </div>

      {noise && noise.opacity > 0 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url("https://framerusercontent.com/images/g0QcWrxr87K0ufOxIUFBakwYA8.png")`,
            backgroundSize: noise.scale * 200,
            backgroundRepeat: 'repeat',
            opacity: noise.opacity / 2
          }}
        />
      )}
    </div>
  )
}
